import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { buildUrlWithParams } from '@/requests/url';
// eslint-disable-next-line prettier/prettier
import {
  isPage,
  isPageKey,
} from '@/requests/api';
// eslint-disable-next-line prettier/prettier
import type {
  Page,
  PageDetail,
  PageApiV44,
  PageApiV49,
} from '@/requests/api';
import {
  activePage,
  getServices,
  getAllServicesRaw,
  login,
  pageInfo,
  PageState,
  refreshPage,
  search,
} from '@/requests/api';
import type { AnyRatingService, AnyService } from '@/stores/ratingService';
import type { ServiceHash } from '@/stores/ImageMetadata';
import { sleep } from '@/utils/sleep';
import {
  API_ARCHIVE_IF_NOT_WORST,
  API_HOST,
  API_KEY,
  API_TAG_READ_SERVICE_ID,
  API_TAG_WRITE_SERVICE_ID,
  API_VOTE_SERVICE_IDS,
  SETTINGS_ALLOW_EXIT_WHILE_VOTING,
  SETTINGS_ARCHIVAL_MODE,
  SETTINGS_ARCHIVAL_RULES,
  SETTINGS_CBOR_TRANSPORT_ENABLED,
  SETTINGS_FAVORITE_TAG_SORT_MODE,
  SETTINGS_HISTORY_CHECKPOINT_IMAGES,
  SETTINGS_HISTORY_CHECKPOINT_SECONDS,
  SETTINGS_MOUSE_VOTES,
  SETTINGS_REDUCED_HISTORY_STEPS,
  SETTINGS_RETRY_AUTO_ENABLED,
  SETTINGS_RETRY_INITIAL_DELAY,
  SETTINGS_RETRY_MAX_ATTEMPTS,
  SETTINGS_THEME,
  SETTINGS_ZOOM_ENABLED,
  SETTINGS_ZOOM_PLAYBACK_SYNC,
  getLocalStorageItem,
  setLocalStorageItem,
} from '@/stores/localStorage';
import type { ArchivalMode, CustomArchivalRules } from '@/utils/archival';
import { defaultArchivalMode, defaultArchivalRules, defaultVoteServiceIds } from '@/utils/envDefaults';

export type Image = { id: number; src: string };

export enum SpecialPage {
  SEARCH = -1,
  CURRENT = -2,
}

export type SelectedPage = Page | null | SpecialPage;

/** recursively go through the pages, return the one with the given key */
function getPageByKey(key: Page['page_key'], page: Page): Page | null {
  if (page.page_key == key) {
    return page;
  }
  for (const pageIterator of page.pages || []) {
    const returnPage = getPageByKey(key, pageIterator);
    if (returnPage) {
      return returnPage;
    }
  }
  return null;
}

export const useApiStore = defineStore('api', () => {
  function normalizeHistoryCheckpointSeconds(value: unknown): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || Number.isNaN(numericValue)) {
      return 60;
    }
    return Math.max(0, Math.floor(numericValue));
  }

  function normalizeHistoryCheckpointImages(value: unknown): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || Number.isNaN(numericValue)) {
      return 10;
    }
    return Math.max(1, Math.floor(numericValue));
  }

  const host = ref<string>(
    getLocalStorageItem(API_HOST, 'http://localhost:45869'),
  );
  watch(
    () => host.value,
    (newVal) => setLocalStorageItem(API_HOST, newVal),
  );

  const apiKey = ref<string>(
    // eslint-disable-next-line prettier/prettier
    getLocalStorageItem(
      API_KEY,
      '',
    ),
  );
  watch(
    () => apiKey.value,
    (newVal) => setLocalStorageItem(API_KEY, newVal),
  );

  const tagReadServiceId = ref<string>(
    // eslint-disable-next-line prettier/prettier
    getLocalStorageItem(
      API_TAG_READ_SERVICE_ID,
      '616c6c206b6e6f776e2074616773',
    ),
  );
  watch(
    () => tagReadServiceId.value,
    (newVal) => setLocalStorageItem(API_TAG_READ_SERVICE_ID, newVal),
  );

  const tagWriteServiceId = ref<string>(
    // eslint-disable-next-line prettier/prettier
    getLocalStorageItem(
      API_TAG_WRITE_SERVICE_ID,
      '6c6f63616c2074616773',
    ),
  );
  watch(
    () => tagWriteServiceId.value,
    (newVal) => setLocalStorageItem(API_TAG_WRITE_SERVICE_ID, newVal),
  );

  const voteServiceIds = ref<ServiceHash[]>(
    getLocalStorageItem(API_VOTE_SERVICE_IDS, defaultVoteServiceIds(['9d3ed76245b5b5d88c6b6c3ef71fbced62f5b1245f8c32a0ab21db85421d629a', '4f369bd0934cd746b212f750e03dd0faedba4ba81f93132704e9fdf9d07982be'])),
  );
  watch(
    () => voteServiceIds.value,
    (newVal) => setLocalStorageItem(API_VOTE_SERVICE_IDS, newVal),
    { deep: true },
  );

  const archiveIfNotWorst = ref<boolean>(
    // eslint-disable-next-line prettier/prettier
    getLocalStorageItem(
        API_ARCHIVE_IF_NOT_WORST,
        true,
    ),
  );

  const sessionKey = ref<string | null>(null);
  watch(apiKey, () => (sessionKey.value = null)); // make sure the session key will reset when the main key is changed.

  const fileIds = ref<number[]>([]);

  const services = ref<{ [key: ServiceHash]: AnyRatingService } | null>(null);
  watch(voteServiceIds, () => (services.value = null)); // make sure the downloaded service information will reset when the main key is changed.

  const availableServices = ref<Record<string, AnyService> | null>(null);
  const isLoadingAvailableServices = ref(false);

  async function refreshAvailableServices(): Promise<void> {
    isLoadingAvailableServices.value = true;
    try {
      availableServices.value = await getAllServicesRaw();
    } finally {
      isLoadingAvailableServices.value = false;
    }
  }

  const pages = ref<Page | null>(null);
  /** Cached file counts per page key, populated when a page is loaded. */
  const pageFileCounts = ref<Record<string, number>>({});
  /** Selected page in the client */
  const clientPage = ref<Page | null>(null);
  /** The one we have selected in here */
  const selectedPage = ref<SelectedPage>(null);
  const searchQuery = ref<string>();

  async function searchAndApply(query: string) {
    const info = await search(query);
    setFileIds(info);
    console.debug('loading search from source:', info);
  }

  // make the normal pages and search load newly on change
  watch(
    () => [selectedPage.value, searchQuery.value],
    async ([page, query]: [Page, string], [oldPage, oldQuery]: [Page, string]) => {
      console.log('page: ', page);
      if (oldPage === page && oldQuery === query) {
        console.debug('No page/search to load, is same.');
        return;
      }
      if (page === null || page === undefined) {
        console.debug('No page or search to load, page is null/undefined.');
        return;
      }
      await loadAndApplyFileIds(page, query);
    },
  );

  async function loadAndApplyFileIds(page: SelectedPage, query: string | undefined) {
    if (page === SpecialPage.SEARCH) {
      if (query === null || query === undefined) {
        console.debug('No page to load, search is empty.');
        return;
      }
      await searchAndApply(query);
      return;
    }
    if (page === null) {
      console.debug('No page to load, is now null.');
      setFileIds([]);
      return;
    }

    const pageKeyToLoad = replaceCurrentSelection(page);
    const pageInfo = await getFileIdsWhenPageLoaded(pageKeyToLoad);
    if (pageInfo !== null) {
      pageFileCounts.value = {
 ...pageFileCounts.value,
[pageKeyToLoad]: pageInfo.media.num_files,
};
      setFileIds(pageInfo.media.hash_ids);
    }
  }

  function replaceCurrentSelection(page: PageApiV44 | PageApiV49 | SpecialPage.CURRENT) {
    let pageKeyToLoad = page.page_key;

    if (
      page === SpecialPage.CURRENT &&
      isPage(clientPage.value) &&
      (clientPage.value as Page).page_key !== undefined
    ) {
      pageKeyToLoad = (clientPage.value as Page).page_key;
    }
    return pageKeyToLoad;
  }

  function setSessionKey(key: string) {
    sessionKey.value = key;
  }

  function setFileIds(ids: number[]) {
    fileIds.value = ids;
  }

  async function ensureSession() {
    console.log('sessionKey, before:', sessionKey.value);
    if (!sessionKey.value) {
      sessionKey.value = await login();
    }
    console.log('sessionKey, after: ', sessionKey.value);
  }

  async function ensureService() {
    console.log('service, before:', services.value);
    if (services.value === null) {
      const result = await getServices();
      console.log('service, data:', result);
      services.value = result;
    }
    console.log('service, after: ', services.value);
  }

  async function refreshAndLoadFileIds() {
    await ensureSession();
    await ensureService();

    const page: SelectedPage = selectedPage.value;
    console.log('page to refresh: ', page);
    if (page === null) {
      console.warn('No page to refresh & reload.');
      return;
    }
    if (page === SpecialPage.SEARCH) {
      const query = searchQuery.value;
      if (query === '' || query === undefined) {
        setFileIds([]);
        return;
      }
      await searchAndApply(query);
      return;
    }
    // else: proper Page
    const pageToRefreshKey = replaceCurrentSelection(page);
    // eslint-disable-next-line prettier/prettier
    console.log('refreshAndLoadFileIds', {
 pageToRefreshKey,
page,
pageKey: pageKey.value,
});

    // make hyrus refresh the page
    await refreshPage(pageToRefreshKey);
    console.log('page refreshed');
    // loading images etc. is not (?) handled by watcher on `selectedPage.value`.
    const pageInfo = await getFileIdsWhenPageLoaded(pageToRefreshKey);
    if (pageInfo !== null) {
      setFileIds(pageInfo.media.hash_ids);
    }
    console.log('page reloaded');
  }

  function reset() {
    fileIds.value = [];
  }

  /**
   * Initial loading of the images.
   */
  async function loadFileIds() {
    await ensureSession();
    await ensureService();

    const activePageInfo = await activePage();
    pages.value = activePageInfo.all;
    clientPage.value = activePageInfo.current;
    let pageToSelect = activePageInfo.current;

    // if we have a pageKey in the localStorage, load that one instead
    if (pageKey.value) {
      // eslint-disable-next-line prettier/prettier
      pageToSelect = getPageByKey(pageKey.value, activePageInfo.all) || pageToSelect;
    }
    selectedPage.value = pageToSelect;
    // loading images etc. is handled by watcher on `selectedPage.value`.
  }

  /**
   * Refresh the page every 2.5 seconds, until the state is `PageState.READY` (`0`) or `PageState.CANCELLED` (`3`).
   * @param pageKey The page to check (and apply)
   * @return  The loaded page if the state was `PageState.READY` (`0`), or null if it was any other non-loading state `PageState.CANCELLED` (`3`).
   */
  async function getFileIdsWhenPageLoaded(
    pageKey: string,
  ): Promise<PageDetail | null> {
    /**
     * page_state is as follows:
     *
     * 0 - ready
     * 1 - initialising
     * 2 - searching/loading
     * 3 - search cancelled
     */
    console.log(`setFileIdsWhenPageLoaded(${pageKey})`);
    let pageState: number | null = null;
    let info: PageDetail | null = null;
    while (
      info === null ||
      pageState === null ||
      pageState === PageState.INITIALISING ||
      pageState === PageState.LOADING
    ) {
      info = await pageInfo(pageKey);
      console.log('info: ', {
 pageKey,
info,
});
      pageState = info.page_state;
      if (pageState === PageState.READY) {
        return info;
      }
      await sleep(2.5);
    }
    return null;
  }

  const fileUrls = computed<Image[]>((): Image[] =>
    fileIds.value.map((fileId: number) => ({
      id: fileId,
      src: buildUrlWithParams(host.value + '/get_files/file', {
        file_id: fileId.toString(),
        'Hydrus-Client-API-Session-Key': sessionKey.value,
      }).toString(),
    })),
  );

  const hasSeenVoteAnimation = ref<boolean>(false);
  const mouseVotes = ref<boolean>(
    getLocalStorageItem(SETTINGS_MOUSE_VOTES, false),
  );
  watch(
    () => mouseVotes.value,
    (newVal) => setLocalStorageItem(SETTINGS_MOUSE_VOTES, newVal),
  );

  const defaultZoomEnabled = !window.matchMedia('(pointer: coarse)').matches;
  const zoomEnabled = ref<boolean>(
    getLocalStorageItem(SETTINGS_ZOOM_ENABLED, defaultZoomEnabled),
  );
  watch(
    () => zoomEnabled.value,
    (newVal) => setLocalStorageItem(SETTINGS_ZOOM_ENABLED, newVal),
  );

  const zoomPlaybackSync = ref<boolean>(
    getLocalStorageItem(SETTINGS_ZOOM_PLAYBACK_SYNC, true),
  );
  watch(
    () => zoomPlaybackSync.value,
    (newVal) => setLocalStorageItem(SETTINGS_ZOOM_PLAYBACK_SYNC, newVal),
  );

  const allowExitWhileVoting = ref<boolean>(
    getLocalStorageItem(SETTINGS_ALLOW_EXIT_WHILE_VOTING, false),
  );
  watch(
    () => allowExitWhileVoting.value,
    (newVal) => setLocalStorageItem(SETTINGS_ALLOW_EXIT_WHILE_VOTING, newVal),
  );

  const cborEnabled = ref<boolean>(
    getLocalStorageItem(SETTINGS_CBOR_TRANSPORT_ENABLED, false),
  );
  watch(
    () => cborEnabled.value,
    (newVal) => setLocalStorageItem(SETTINGS_CBOR_TRANSPORT_ENABLED, newVal),
  );

  type FavoriteTagSortMode = 'fixed' | 'recent';
  const favoriteTagSortMode = ref<FavoriteTagSortMode>(
    getLocalStorageItem(SETTINGS_FAVORITE_TAG_SORT_MODE, 'fixed'),
  );
  watch(
    () => favoriteTagSortMode.value,
    (newVal) => setLocalStorageItem(SETTINGS_FAVORITE_TAG_SORT_MODE, newVal),
  );

  const archivalMode = ref<ArchivalMode>(
    getLocalStorageItem(SETTINGS_ARCHIVAL_MODE, defaultArchivalMode('vote-exists-both')),
  );
  watch(
    () => archivalMode.value,
    (newVal) => setLocalStorageItem(SETTINGS_ARCHIVAL_MODE, newVal),
  );

  const customArchivalRules = ref<CustomArchivalRules>(
    getLocalStorageItem(SETTINGS_ARCHIVAL_RULES, defaultArchivalRules({
 archiveCondition: null,
unarchiveCondition: null,
})),
  );
  watch(
    () => customArchivalRules.value,
    (newVal) => setLocalStorageItem(SETTINGS_ARCHIVAL_RULES, newVal),
    { deep: true },
  );

  // Retry settings for network error handling
  const retryMaxAttempts = ref<number>(
    getLocalStorageItem(SETTINGS_RETRY_MAX_ATTEMPTS, 15),
  );
  watch(
    () => retryMaxAttempts.value,
    (newVal) => setLocalStorageItem(SETTINGS_RETRY_MAX_ATTEMPTS, Math.max(1, Math.min(newVal, 50))),
  );

  const retryInitialDelay = ref<number>(
    getLocalStorageItem(SETTINGS_RETRY_INITIAL_DELAY, 2000),
  );
  watch(
    () => retryInitialDelay.value,
    (newVal) => setLocalStorageItem(SETTINGS_RETRY_INITIAL_DELAY, Math.max(100, Math.min(newVal, 10000))),
  );

  const retryAutoEnabled = ref<boolean>(
    getLocalStorageItem(SETTINGS_RETRY_AUTO_ENABLED, true),
  );
  watch(
    () => retryAutoEnabled.value,
    (newVal) => setLocalStorageItem(SETTINGS_RETRY_AUTO_ENABLED, newVal),
  );

  type ThemeMode = 'dark' | 'light' | 'auto';
  const themeMode = ref<ThemeMode>(getLocalStorageItem(SETTINGS_THEME, 'auto'));
  const reducedHistoryStepsEnabled = ref<boolean>(
    getLocalStorageItem(SETTINGS_REDUCED_HISTORY_STEPS, true),
  );
  watch(
    () => reducedHistoryStepsEnabled.value,
    (newVal) => setLocalStorageItem(SETTINGS_REDUCED_HISTORY_STEPS, newVal),
  );

  const historyCheckpointSeconds = ref<number>(
    normalizeHistoryCheckpointSeconds(
      getLocalStorageItem(SETTINGS_HISTORY_CHECKPOINT_SECONDS, 60),
    ),
  );
  watch(
    () => historyCheckpointSeconds.value,
    (newVal) => {
      const normalizedValue = normalizeHistoryCheckpointSeconds(newVal);
      if (normalizedValue !== newVal) {
        historyCheckpointSeconds.value = normalizedValue;
        return;
      }
      setLocalStorageItem(SETTINGS_HISTORY_CHECKPOINT_SECONDS, normalizedValue);
    },
  );

  const historyCheckpointImages = ref<number>(
    normalizeHistoryCheckpointImages(
      getLocalStorageItem(SETTINGS_HISTORY_CHECKPOINT_IMAGES, 10),
    ),
  );
  watch(
    () => historyCheckpointImages.value,
    (newVal) => {
      const normalizedValue = normalizeHistoryCheckpointImages(newVal);
      if (normalizedValue !== newVal) {
        historyCheckpointImages.value = normalizedValue;
        return;
      }
      setLocalStorageItem(SETTINGS_HISTORY_CHECKPOINT_IMAGES, normalizedValue);
    },
  );

  function applyTheme(mode: ThemeMode) {
    if (mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }

  // Listen for OS-level preference changes when in auto mode
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (themeMode.value === 'auto') applyTheme('auto');
  });

  watch(
    () => themeMode.value,
    (newVal) => {
      setLocalStorageItem(SETTINGS_THEME, newVal);
      applyTheme(newVal);
    },
    { immediate: true },
  );

  const LOCALSTORAGE_VIEWER_MODE_KEY = 'viewerMode';
  type viewerModeType = 'single' | 'desktop' | 'mobile';
  function isViewerMode(val: any): val is viewerModeType {
    return typeof val === 'string' && ['single', 'desktop', 'mobile'].indexOf(val) !== -1;
  }
  const defaultViewerMode = window.innerHeight > window.innerWidth ? 'mobile' : 'desktop';
  const storageViewerMode = localStorage.getItem(LOCALSTORAGE_VIEWER_MODE_KEY);
  const initialViewerMode = isViewerMode(storageViewerMode) ? storageViewerMode : defaultViewerMode;
  const viewerMode = ref<viewerModeType>(initialViewerMode);
  watch(
      () => viewerMode.value,
      (newViewerMode) => localStorage.setItem(LOCALSTORAGE_VIEWER_MODE_KEY, newViewerMode),
  );

  // eslint-disable-next-line prettier/prettier
  type PageKey = SelectedPage extends Page ? Page['page_key'] | Exclude<SelectedPage, Page> : never;
  const LOCALSTORAGE_PAGE_KEY = 'page';
  const storagePageKey: PageKey = localStorage.getItem(LOCALSTORAGE_PAGE_KEY);
  // eslint-disable-next-line prettier/prettier
  const initialPageKey: PageKey = isPageKey(storagePageKey) ? storagePageKey : null;
  const pageKey = ref<PageKey>(initialPageKey);
  // eslint-disable-next-line prettier/prettier
  console.log('PAGE KEY INIT', {
 LOCALSTORAGE_PAGE_KEY,
storagePageKey,
initialPageKey,
pageKey: pageKey.value,
});

  watch(
    () => selectedPage.value,
    (newSelectedPage: SelectedPage) => {
      // eslint-disable-next-line prettier/prettier
      pageKey.value = isPage(newSelectedPage) ? newSelectedPage.page_key : newSelectedPage;
    },
  );

  watch(
    () => pageKey.value,
    (newPageKey: PageKey) => {
      localStorage.setItem(LOCALSTORAGE_PAGE_KEY, newPageKey);
    },
  );

  return {
    host,
    apiKey,
    pages,
    pageFileCounts,
    selectedPage,
    clientPage,
    searchQuery,
    tagReadServiceId,
    tagWriteServiceId,
    reset,
    sessionKey,
    setSessionKey,
    voteServiceIds,
    archiveIfNotWorst,
    setFileIds,
    fileUrls,
    ensureSession,
    ensureService,
    loadFileIds,
    refreshAndLoadFileIds,
    services,
    availableServices,
    isLoadingAvailableServices,
    refreshAvailableServices,
    hasSeenVoteAnimation,
    mouseVotes,
    zoomEnabled,
    zoomPlaybackSync,
    allowExitWhileVoting,
    cborEnabled,
    favoriteTagSortMode,
    archivalMode,
    customArchivalRules,
    retryMaxAttempts,
    retryInitialDelay,
    retryAutoEnabled,
    reducedHistoryStepsEnabled,
    historyCheckpointSeconds,
    historyCheckpointImages,
    themeMode,
    viewerMode,
    pageKey,
  };
});
